import { Lecture, } from "../../types/lecture";
import { LectureServiceInterface } from "./LectureServiceInterface";
export class LectureServiceLocal extends LectureServiceInterface {
    lectures = new Map();
    async getAll() {
        return Array.from(this.lectures.values())
            .filter((l) => !l.deletedInSAC)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByDateRange(startDate, endDate) {
        return Array.from(this.lectures.values())
            .filter((lecture) => !lecture.deletedInSAC &&
            lecture.lectureDate >= startDate &&
            lecture.lectureDate <= endDate)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByTeacher(teacherId, startDate, endDate) {
        return Array.from(this.lectures.values())
            .filter((l) => !l.deletedInSAC &&
            l.teacherId === teacherId &&
            l.lectureDate >= startDate &&
            l.lectureDate <= endDate)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByClassId(classroomId) {
        return Array.from(this.lectures.values())
            .filter((lecture) => !lecture.deletedInSAC &&
            lecture.classroomId === classroomId)
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByClassIds(classroomIds) {
        const uniqueIds = [...new Set(classroomIds)];
        return Array.from(this.lectures.values())
            .filter((lecture) => !lecture.deletedInSAC &&
            uniqueIds.includes(lecture.classroomId))
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getByTerm(year, term, includeDeletedInSAC = false) {
        return Array.from(this.lectures.values())
            .filter((lecture) => (includeDeletedInSAC || !lecture.deletedInSAC) &&
            String(lecture.year) === String(year) &&
            String(lecture.term) === String(term))
            .sort((a, b) => a.lectureDate.getTime() - b.lectureDate.getTime());
    }
    async getLecture(lectureId) {
        const lecture = this.lectures.get(lectureId);
        if (!lecture) {
            throw new Error(`Lecture with id ${lectureId} not found`);
        }
        return lecture;
    }
    /** @deprecated All lectures are created via syncFromSAC. This method is no longer used. */
    async createLecture(lectureData) {
        const newLecture = new Lecture(lectureData);
        this.lectures.set(newLecture.id, newLecture);
        return newLecture;
    }
    async startLecture(lectureId, teacherId) {
        const lecture = await this.getLecture(lectureId);
        lecture.start(teacherId);
        this.lectures.set(lectureId, lecture);
        return lecture;
    }
    async completeLecture(lectureId) {
        const lecture = await this.getLecture(lectureId);
        lecture.complete();
        this.lectures.set(lectureId, lecture);
        return lecture;
    }
    async updateLecture(lectureId, lectureData) {
        const lecture = await this.getLecture(lectureId);
        // Only allow updating specific fields
        const updatableData = {
            classroomId: lectureData.classroomId ?? lecture.classroomId,
            topicIds: lectureData.topicIds ?? lecture.topicIds,
            weekNumber: lectureData.weekNumber ?? lecture.weekNumber,
            lectureDate: lectureData.lectureDate ?? lecture.lectureDate,
            lectureEndDate: lectureData.lectureEndDate ?? lecture.lectureEndDate,
            topicsCovered: lectureData.topicsCovered ?? lecture.topicsCovered ?? null,
        };
        lecture.update(updatableData);
        this.lectures.set(lectureId, lecture);
        return lecture;
    }
    async setTournamentId(lectureId, tournamentId) {
        const lecture = await this.getLecture(lectureId);
        lecture.tournamentId = tournamentId;
        this.lectures.set(lectureId, lecture);
    }
    async addWarmupGame(lectureId, gameData) {
        const lecture = await this.getLecture(lectureId);
        const warmupGame = lecture.addWarmupGame(gameData);
        this.lectures.set(lectureId, lecture);
        return warmupGame;
    }
    async updateWarmupGameResult(lectureId, gameId, whiteResult, blackResult) {
        const lecture = await this.getLecture(lectureId);
        lecture.updateWarmupGameResult(gameId, whiteResult, blackResult);
        const updatedGame = lecture.warmupGames.find((g) => g.id === gameId);
        if (!updatedGame) {
            throw new Error(`Warm-up game ${gameId} not found`);
        }
        this.lectures.set(lectureId, lecture);
        return updatedGame;
    }
    async cancelWarmupGame(lectureId, gameId) {
        const lecture = await this.getLecture(lectureId);
        lecture.cancelWarmupGame(gameId);
        const cancelledGame = lecture.warmupGames.find((g) => g.id === gameId);
        if (!cancelledGame) {
            throw new Error(`Warm-up game ${gameId} not found`);
        }
        this.lectures.set(lectureId, lecture);
        return cancelledGame;
    }
    async getWarmupGames(lectureId) {
        const lecture = await this.getLecture(lectureId);
        return lecture.warmupGames;
    }
    async getActiveWarmupGames(lectureId) {
        const lecture = await this.getLecture(lectureId);
        return lecture.getActiveWarmupGames();
    }
    async saveLecture(lecture) {
        // For local service, just store the lecture in memory
        this.lectures.set(lecture.id, lecture);
        return lecture;
    }
    async syncFromSAC(_teacherId, _sacLectures, _windowStart, _windowEnd) {
        return { deleted: 0, synced: 0 };
    }
}
